
/* eslint-disable @typescript-eslint/camelcase */
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'

import { DealCardType, TextFieldRules, DealType } from '@/utils/enums'
import { Card } from '@/types/Card'
import cardModule from '@/store/card'
import authModule from '@/store/auth'
import whitelabelModule from '@/store/whitelabel'
import dealModule from '@/store/deal'
import { compareNested, uploadImage } from '@/utils/utility-manager'
import axios from 'axios'

@Component({
  name: 'CardEditor'
})
class CardEditor extends Vue {
  @Prop() readonly edit?: boolean
  private rules = TextFieldRules
  private dealCardType = DealCardType
  private types = Object.entries(DealCardType)
  private categories: any[] = []
  private tags: any[] = []
  private currentImage = ''
  private form: Partial<Card> = {
    type: '',
    description: '',
    image: '',
    title_big: '',
    title_small: '',
    feed_title_big: '',
    feed_title_small: '',
    teaser: '',
    deals: [''],
    cards: [''],
    categories: [],
    url: '',
    dealCategory: [],
    dealTags: [],
    dealCity: null,
    dealEndDateFrom: '',
    dealEndDateTo: ''
  }

  private get auth() {
    return authModule
  }

  private get card() {
    return cardModule.card!
  }

  private get whitelabel() {
    return whitelabelModule.whitelabel
  }

  private get whitelabels() {
    return whitelabelModule.whitelabels
  }

  private get deals() {
    return dealModule.deals
  }

  private get cards() {
    return cardModule.cardsUnfiltered
  }

  private get cardImage() {
    return this.form.image
  }

  private get filteredDeals() {
    if (
      (this.form.type!.includes(DealCardType.LOCAL) &&
        this.form.type!.includes(DealCardType.ONLINE)) ||
      this.form.type!.includes(DealCardType.SPECIFIC)
    ) {
      return this.deals.filter(el => el.type !== DealType.REWARDS)
    }
    if (this.form.type!.includes(DealCardType.LOCAL)) {
      return this.deals.filter(el => el.type === DealType.LOCAL)
    }
    if (this.form.type!.includes(DealCardType.ONLINE)) {
      return this.deals.filter(el => el.type === DealType.ECOMMERCE)
    }
    return []
  }

  private get filteredCards() {
    if (this.form.type!.includes(DealCardType.LOCAL)) {
      return this.cards.filter(el => el.type !== DealCardType.ONLINE)
    }
    if (this.form.type!.includes(DealCardType.ONLINE)) {
      return this.cards.filter(el => el.type !== DealCardType.LOCAL)
    }
    return []
  }

  private get filteredCategories() {
    if (
      this.form.type!.includes(DealCardType.LOCAL) &&
      this.form.type!.includes(DealCardType.ONLINE)
    ) {
      return this.categories
    }
    if (this.form.type!.includes(DealCardType.LOCAL)) {
      return this.categories.filter(el => el.type === DealCardType.LOCAL)
    }
    if (this.form.type!.includes(DealCardType.ONLINE)) {
      return this.categories.filter(el => el.type === DealCardType.ONLINE)
    }
    return []
  }

  private get filteredTags() {
    return this.tags
  }

  private get noDealsText(): string | undefined {
    return this.form.type!.length === 0
      ? 'Bitte wählen Sie einen Typen aus'
      : undefined
  }

  private get noCardsText(): string | undefined {
    return this.form.type!.length === 0
      ? 'Bitte wählen Sie einen Typen aus'
      : undefined
  }

  @Watch('card')
  private async load() {
    await dealModule.fetchAll({ limit: 0, lean: true })
    await cardModule.fetchUnfiltered()

    const { data } = await axios.get(
      `/admin/${this.auth.user!.whitelabel.slug}/category/list`
    )
    this.categories = data.categories

    const { data: tagdata } = await axios.get(
      `/admin/${this.auth.user!.whitelabel.slug}/tag/list`
    )
    this.tags = tagdata.tags

    if (!this.edit) return
    if (!this.card) {
      await cardModule.fetch(this.$route.params.cid)
    }

    this.form = JSON.parse(JSON.stringify(this.card))
    if (this.form.image && this.form.image.length > 0) {
      this.currentImage = this.form.image
    }
  }

  private async created(): Promise<void> {
    await this.load()
  }

  private checkType(): void {
    if (this.form.type === DealCardType.SPECIFIC) {
      if (this.form.deals!.length > 0) {
        this.form.deals = [this.form.deals!.shift()!]
      } else {
        this.form.deals = ['']
      }
    }
  }

  private addDeal(): void {
    this.form.deals!.push('')
  }

  private removeDeal(index: number): void {
    this.form.deals!.splice(index, 1)
  }

  private addCard(): void {
    this.form.cards!.push('')
  }

  private removeCard(index: number): void {
    this.form.cards!.splice(index, 1)
  }

  private async addImage(file: File): Promise<void> {
    if (!this.isImage(file)) return
    const result = await uploadImage(file)

    this.form.image = result[0].key
    this.currentImage = result[0].url
  }

  private resetImage(): void {
    this.form.image = ''
  }

  private isImage(file: File): boolean {
    if (!file) return false
    return file.type.split('/')[0] === 'image'
  }

  private sanitizeForm() {
    if (this.form.deals) this.form.deals = this.form.deals.filter(d => d !== '')
    if (this.form.cards) this.form.cards = this.form.cards.filter(c => c !== '')
  }

  private async saveCard(): Promise<void> {
    this.sanitizeForm()
    if (!this.edit) {
      cardModule.create({
        whitelabel: this.whitelabel._id,
        card: this.form
      })
    } else {
      const payload: Partial<Card> = await compareNested(this.form, this.card)
      Object.assign(payload, { _id: this.card!._id })
      cardModule.update({ whitelabel: this.card.whitelabel, card: payload })
    }
    document.dispatchEvent(new Event('close-card'))
  }
}

export default CardEditor
